import { useState } from 'react'
import { TextInput, Button } from '@datahub/design-system'
import { post } from '../lib/api'
import type { AdminUser } from '../lib/types'
import { StearsBackdrop } from '../components/StearsBackdrop'

/** Form/Auth pattern. Email and password only; there is no sign-up (ADM-01, ADM-06). */
export function SignIn({ onSignedIn }: { onSignedIn: (admin: AdminUser) => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)
  const [mode, setMode] = useState<'signin' | 'forgot'>('signin')

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      if (mode === 'forgot') {
        await post('/auth/password/forgot', { email })
        setForgotSent(true)
      } else {
        const res = await post<{ admin: AdminUser }>('/auth/login', { email, password })
        onSignedIn(res.admin)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work')
    } finally {
      setBusy(false)
    }
  }

  return (
    <StearsBackdrop>
      <div style={{ padding: '20px 20px 0' }}>
        <img src="/images/stears-logo-white.webp" alt="Stears" style={{ height: 24, width: 'auto' }} />
      </div>
      <div className="flex" style={{ minHeight: 'calc(100% - 44px)', flexWrap: 'wrap' }}>
        <div className="flex flex-col justify-center" style={{ flex: '1 1 360px', padding: 48, gap: 20 }}>
          <h1 className="font-jakarta font-bold text-white" style={{ fontSize: 20, lineHeight: '24px' }}>Stears Datahub</h1>
          <p className="font-mulish text-text-description" style={{ fontSize: 12, lineHeight: '22px', maxWidth: 420 }}>
            The internal tool where companies keep their own transaction, ownership, management and financial data up to
            date with Stears, on a reporting cadence you set.
          </p>
          <ul className="flex flex-col" style={{ gap: 10, listStyle: 'none', padding: 0, margin: 0, maxWidth: 420 }}>
            {['Track responses across companies and cadences', 'Read every submission row by row', 'Copy accepted rows into the Core database'].map((point) => (
              <li key={point} className="flex" style={{ gap: 10 }}>
                <span style={{ color: '#85BEFF', fontSize: 12, lineHeight: '22px' }}>✓</span>
                <span className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '22px' }}>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col justify-center" style={{ flex: '1 1 420px', padding: 48 }}>
          <div style={{ maxWidth: 420, width: '100%' }}>
            <h2 className="font-jakarta font-bold text-white" style={{ fontSize: 20, lineHeight: '24px', marginBottom: 8 }}>
              {mode === 'forgot' ? 'Reset your password' : 'Sign in'}
            </h2>
            <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px', marginBottom: 24 }}>
              {mode === 'forgot'
                ? 'Enter your email and we will send a reset link if the address belongs to a Datahub admin.'
                : 'Access is by invitation. Ask a Datahub admin if you need an account.'}
            </p>

            {forgotSent ? (
              <p className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '22px' }}>
                If that email belongs to a Datahub admin, a reset link is on its way. It expires in 60 minutes.
              </p>
            ) : (
              <form
                className="flex flex-col"
                style={{ gap: 20 }}
                onSubmit={(e) => { e.preventDefault(); void submit() }}
              >
                <TextInput label="Email" size="large" inputType="email" value={email} onChange={setEmail} placeholder="you@stears.co" />
                {mode === 'signin' && (
                  <TextInput label="Password" size="large" inputType="password" value={password} onChange={setPassword} />
                )}
                {error && (
                  <span className="font-mulish" style={{ fontSize: 10, lineHeight: '20px', color: '#F05042' }}>{error}</span>
                )}
                <div style={{ paddingTop: 4 }}>
                  <Button variant="filled-grey" size="medium" className="w-full" loading={busy} onClick={() => void submit()}>
                    {mode === 'forgot' ? 'Send reset link' : 'Sign in'}
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => { setMode(mode === 'signin' ? 'forgot' : 'signin'); setError(null) }}
                  className="font-jakarta text-text-hyperlink hover:underline self-start"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12 }}
                >
                  {mode === 'signin' ? 'Forgot password' : 'Back to sign in'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </StearsBackdrop>
  )
}
